import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Plus,
  MoreHorizontal,
  Edit,
  Mail,
  UserCog,
  Shield,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function SubAdminManagement() {
  const [users, setUsers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    user_role: 'sub_admin'
  });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      const me = await api.auth.me();
      const systemAdmin = me?.user_role === 'admin' || me?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);

        if (defaultEmployerId) {
          const allUsers = await api.entities.User.filter({ employer_id: defaultEmployerId });
          const subAdmins = allUsers.filter(u => u.user_role === 'sub_admin' || u.user_role === 'admin');
          setUsers(subAdmins);
        } else {
          setUsers([]);
        }
      } else {
        const allUsers = await api.entities.User.list();
        const subAdmins = allUsers.filter(u => u.user_role === 'sub_admin' || u.user_role === 'admin');
        setUsers(subAdmins);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      const allUsers = await api.entities.User.filter({ employer_id: value });
      const subAdmins = allUsers.filter(u => u.user_role === 'sub_admin' || u.user_role === 'admin');
      setUsers(subAdmins);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        user_role: user.user_role || 'sub_admin'
      });
    } else {
      setEditingUser(null);
      setFormData({
        full_name: '',
        email: '',
        user_role: 'sub_admin'
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (editingUser) {
      try {
        await api.entities.User.update(editingUser.id, {
          user_role: formData.user_role
        });
        
        await api.entities.AuditLog.create({
          entity_type: 'User',
          entity_id: editingUser.id,
          action: 'update',
          performed_by: (await api.auth.me()).email,
          notes: `Updated user role to ${formData.user_role}`
        });
        
        toast.success('User updated successfully');
        setShowModal(false);
        init();
      } catch (err) {
        console.error(err);
        toast.error('Failed to update user');
      }
    } else {
      toast.info('To add new sub-admins, please use the invite user functionality from the dashboard settings.');
      setShowModal(false);
    }
  };

  const toggleUserActive = async (user) => {
    try {
      await api.entities.User.update(user.id, {
        is_active: !user.is_active
      });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      init();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update user status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Sub-Admin Management</h1>
          <p className="text-slate-500 mt-1">
            Manage administrator access and permissions
          </p>
        </div>
        <Button 
          onClick={() => openModal()}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Sub-Admin
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-purple-700 dark:text-purple-400 mt-0.5" />
          <div>
            <p className="font-medium text-purple-900 dark:text-purple-100">About Sub-Admins</p>
            <p className="text-sm text-purple-700 dark:text-purple-300">
              Sub-admins have access to most admin features but may have restricted permissions for certain sensitive operations. 
              They can manage employees, benefits, and view reports.
            </p>
          </div>
        </CardContent>
      </Card>

      {isSystemAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((employer) => (
                    <SelectItem key={employer.id} value={employer.id}>
                      {employer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    No sub-admins found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-medium">
                          {user.full_name?.[0] || user.email?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.full_name || 'Unnamed'}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        user.user_role === 'admin' 
                          ? 'default'
                          : 'secondary'
                      }>
                        <UserCog className="w-3 h-3 mr-1" />
                        {user.user_role === 'admin' ? 'Admin' : 'Sub-Admin'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_active === false ? (
                        <Badge variant="destructive">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      ) : (
                        <Badge variant="default">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {user.last_login 
                        ? format(new Date(user.last_login), 'MMM d, yyyy HH:mm')
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openModal(user)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Role
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleUserActive(user)}>
                            {user.is_active === false ? (
                              <>
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Activate
                              </>
                            ) : (
                              <>
                                <XCircle className="w-4 h-4 mr-2" />
                                Deactivate
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit User Role' : 'Add Sub-Admin'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editingUser ? (
              <>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">User</p>
                  <p className="font-medium">{editingUser.full_name}</p>
                  <p className="text-sm text-slate-500">{editingUser.email}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Role</Label>
                  <select
                    className="w-full p-2 border rounded-lg"
                    value={formData.user_role}
                    onChange={(e) => setFormData({ ...formData, user_role: e.target.value })}
                  >
                    <option value="sub_admin">Sub-Admin</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">
                  To add new sub-admins, please use the invite user functionality from the dashboard settings.
                </p>
                <p className="text-sm text-slate-500">
                  Invited users can be assigned the sub-admin role after they accept their invitation.
                </p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            {editingUser && (
              <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600">
                Update Role
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}